import { makeAutoObservable, runInAction } from 'mobx'
import { bindAuth } from '@/api/http'
import { ApiError, toApiError } from '@/api/errors'
import { env } from '@/config/env'
import { clearClientData } from '@/lib/clientData'
import { readJSON, removeKey, writeJSON } from '@/lib/storage'
import { authService } from '@/services'
import type { AuthTokens, LinkedCompany, LoginRequest } from '@/types/api'
import type { RootStore } from './RootStore'

type AuthStatus = 'booting' | 'anonymous' | 'authenticated'

const REFRESH_KEY = 'mf.auth.refresh'

/** Tells the console's other open tabs that the user signed out, so they sign out too. */
const AUTH_CHANNEL = 'mf-auth'

function refreshStore(): Storage {
  return env.refreshStorage === 'session' ? sessionStorage : localStorage
}

export class AuthStore {
  status: AuthStatus = 'booting'
  /** Set when the last session ended without the user asking for it. */
  expiredNotice = false
  /**
   * The credentials matched users in several companies: the server signed in to
   * one of them (is_current) and the login page asks which one to enter.
   * Null outside that step.
   */
  pendingCompanies: LinkedCompany[] | null = null

  // Tokens are deliberately not observable: nothing renders from them.
  private accessToken: string | null = null
  /** The half-open sign-in of the company step; its refresh token is stored only on entering. */
  private pendingTokens: AuthTokens | null = null
  private refreshInFlight: Promise<string | null> | null = null
  private readonly root: RootStore

  constructor(root: RootStore) {
    this.root = root
    makeAutoObservable<AuthStore, 'accessToken' | 'pendingTokens' | 'refreshInFlight' | 'root'>(this, {
      accessToken: false,
      pendingTokens: false,
      refreshInFlight: false,
      root: false,
    })

    bindAuth({
      getAccessToken: () => this.accessToken,
      refresh: () => this.refresh(),
      onSessionExpired: () => this.expire(),
    })

    // Another tab signed out: end this tab's session and wipe it the same way.
    if (typeof BroadcastChannel === 'function') {
      new BroadcastChannel(AUTH_CHANNEL).onmessage = (event: MessageEvent<{ type?: string }>) => {
        if (event.data?.type === 'logout' && this.status === 'authenticated') void this.logout({ broadcast: false })
        // Another tab moved the sign-in to another company: nothing of this one may stay on screen.
        if (event.data?.type === 'company' && this.status === 'authenticated') window.location.replace('/')
      }
    }
  }

  get isAuthenticated() {
    return this.status === 'authenticated'
  }

  /** Restores a session from the stored refresh token, then loads /me/context. */
  async restore() {
    if (!readJSON<string | null>(REFRESH_KEY, null, refreshStore())) {
      // A session that ended while the tab was closed leaves no account's look behind.
      this.root.settings.stop()
      this.setStatus('anonymous')
      return
    }
    const token = await this.refresh()
    if (!token) {
      this.root.settings.stop()
      this.setStatus('anonymous')
      return
    }
    try {
      await this.root.session.load()
      this.startSettings()
      this.setStatus('authenticated')
    } catch {
      this.clear()
      this.setStatus('anonymous')
    }
  }

  /**
   * Signs in. When the credentials match in more than one company the app is
   * not entered yet: pendingCompanies asks the user to pick (chooseCompany).
   */
  async login(request: LoginRequest) {
    const result = await authService.login(request)
    const companies = result.companies ?? []
    if (companies.length > 1) {
      // Only the access token is live; nothing is persisted until a company is entered.
      // A refresh token left from an earlier sign-in would let a background 401
      // "refresh" and wipe this token, so it goes now.
      removeKey(REFRESH_KEY, refreshStore())
      this.accessToken = result.token.access_token
      this.pendingTokens = result.token
      runInAction(() => {
        this.pendingCompanies = companies
      })
      return
    }
    await this.enter(result.token, companies)
  }

  /**
   * Enters the picked company. The one the server signed in to continues as
   * is; another is reached with switch-company (new tokens, no reload: the
   * console has shown nothing yet). A rejection keeps the company step open.
   */
  async chooseCompany(companyId: string) {
    const companies = this.pendingCompanies
    const tokens = this.pendingTokens
    if (!companies || !tokens) return
    if (companies.find((company) => company.id === companyId)?.is_current) {
      await this.enter(tokens, companies)
      return
    }
    try {
      const result = await authService.switchCompany(companyId, tokens.access_token)
      await this.enter(result.token, result.companies ?? companies)
    } catch (error) {
      const apiError = toApiError(error)
      runInAction(() => {
        if (!this.pendingCompanies) return
        // Not linked any more: gone. Stale: listed, but it needs a new sign-in.
        if (apiError.code === 'COMPANY_NOT_LINKED') this.pendingCompanies = this.pendingCompanies.filter((company) => company.id !== companyId)
        if (apiError.code === 'COMPANY_SWITCH_STALE')
          this.pendingCompanies = this.pendingCompanies.map((company) => (company.id === companyId ? { ...company, available: false } : company))
      })
      throw apiError
    }
  }

  /** «Назад» from the company step: the half-open server session is signed out. */
  async cancelCompanyChoice() {
    try {
      if (this.accessToken) await authService.logout()
    } catch {
      /* the session expires on its own */
    } finally {
      this.accessToken = null
      this.pendingTokens = null
      runInAction(() => {
        this.pendingCompanies = null
      })
    }
  }

  private async enter(tokens: AuthTokens, companies: LinkedCompany[]) {
    this.storeTokens(tokens)
    this.pendingTokens = null
    try {
      await this.root.session.load(companies)
    } catch (error) {
      this.clear()
      runInAction(() => {
        this.pendingCompanies = null
      })
      throw toApiError(error)
    }
    this.startSettings()
    runInAction(() => {
      this.pendingCompanies = null
      this.expiredNotice = false
      this.status = 'authenticated'
    })
  }

  /**
   * Single-flight rotation: concurrent 401s share one request, so the same
   * refresh token is never replayed (the backend treats reuse as theft).
   */
  refresh(): Promise<string | null> {
    if (this.refreshInFlight) return this.refreshInFlight

    const refreshToken = readJSON<string | null>(REFRESH_KEY, null, refreshStore())
    if (!refreshToken) return Promise.resolve(null)
    const rotated = this.accessToken

    this.refreshInFlight = authService
      .refresh(refreshToken)
      .then((tokens) => {
        this.storeTokens(tokens)
        return tokens.access_token
      })
      .catch((error: unknown) => {
        const apiError = toApiError(error)
        // A network blip must not destroy a valid session; only an explicit
        // rejection from the server does.
        if (apiError.status === 400 || apiError.status === 401 || apiError.status === 403 || apiError.status === 404) {
          removeKey(REFRESH_KEY, refreshStore())
        }
        // A sign-in that happened meanwhile keeps its token.
        if (this.accessToken === rotated) this.accessToken = null
        return null
      })
      .finally(() => {
        this.refreshInFlight = null
      })

    return this.refreshInFlight
  }

  /**
   * Explicit sign-out: unsaved preferences get their short chance to reach the
   * server, the server session ends, then everything this console kept in the
   * browser is wiped (storage, cookies, caches — see clearClientData) and the
   * page reloads on /login, so no account data survives in memory either.
   * Other open tabs of the console sign out with it.
   */
  async logout({ broadcast = true }: { broadcast?: boolean } = {}) {
    try {
      await this.root.settings.settle()
      if (this.accessToken) await authService.logout()
    } catch (error) {
      if (!(error instanceof ApiError)) throw error
    } finally {
      this.clear()
      this.setStatus('anonymous')
      if (broadcast && typeof BroadcastChannel === 'function') {
        const channel = new BroadcastChannel(AUTH_CHANNEL)
        channel.postMessage({ type: 'logout' })
        channel.close()
      }
      await clearClientData()
      window.location.replace('/login')
    }
  }

  /**
   * Moves the sign-in to another linked company. The server ends the current
   * session and returns a new token pair; both tokens are replaced, every
   * in-memory cache of the old company (lists, lookups, session context,
   * settings) is dropped, and the app reloads on the home route so nothing of
   * the old company stays visible. Other open tabs reload with it.
   * Rejects with the ApiError (COMPANY_NOT_LINKED, COMPANY_SWITCH_STALE, …);
   * on a rejection the current session is untouched.
   */
  async switchCompany(companyId: string) {
    // Unsaved preferences belong to the current profile: give them their chance first.
    await this.root.settings.settle().catch(() => undefined)
    const result = await authService.switchCompany(companyId)
    this.clear()
    this.storeTokens(result.token)
    if (typeof BroadcastChannel === 'function') {
      const channel = new BroadcastChannel(AUTH_CHANNEL)
      channel.postMessage({ type: 'company' })
      channel.close()
    }
    window.location.replace('/')
  }

  expire() {
    if (this.status !== 'authenticated') return
    this.clear()
    this.expiredNotice = true
    this.status = 'anonymous'
  }

  dismissExpiredNotice() {
    this.expiredNotice = false
  }

  private setStatus(status: AuthStatus) {
    this.status = status
  }

  /** The account's UI settings load in the background; the console never waits for them. */
  private startSettings() {
    const profileId = this.root.session.profile?.id
    if (profileId) void this.root.settings.start(profileId)
  }

  private storeTokens(tokens: AuthTokens) {
    this.accessToken = tokens.access_token
    writeJSON(REFRESH_KEY, tokens.refresh_token, refreshStore())
  }

  private clear() {
    this.accessToken = null
    removeKey(REFRESH_KEY, refreshStore())
    this.root.settings.stop()
    this.root.session.clear()
    this.root.lookups.clear()
  }
}
